import * as yup from 'yup';

const DAYS_OF_WEEK = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;

import {
  CloseDeliveries,
  CLOSE_DELIVERIES_OPTIONS,
  OpeningHoursType,
  OPENING_HOURS_TYPES,
} from 'apis/merchant-onboarding-service/types';

import { NormalOpeningHoursEntries } from '../OpeningHoursTab/types';

export const getOverlappingTimespansInfo = (
  openingHours: NormalOpeningHoursEntries,
): { status: boolean; overlappingDay: null | string } => {
  let overlappingDay = null;

  const hasOverlappingTimespans = DAYS_OF_WEEK.some((dayOfWeek, index) => {
    const nextDayOfTheWeek = dayOfWeek === 'sunday' ? 'monday' : DAYS_OF_WEEK[index + 1];
    if (!openingHours[nextDayOfTheWeek].isOpen) {
      return false;
    }
    const closesOnTheNext = openingHours[dayOfWeek].to < openingHours[dayOfWeek].from;
    const isOverlapping = closesOnTheNext && openingHours[nextDayOfTheWeek].from < openingHours[dayOfWeek].to;

    if (isOverlapping) {
      overlappingDay = dayOfWeek;
    }

    return isOverlapping;
  });

  return { status: hasOverlappingTimespans, overlappingDay };
};

const normalOpeningHoursEntrySchema = yup
  .object({
    isOpen: yup.boolean().required(),
    from: yup.number().required(),
    to: yup.number().required(),
  })
  .required();

const normalOpeningHoursSchema = yup
  .object({
    monday: normalOpeningHoursEntrySchema,
    tuesday: normalOpeningHoursEntrySchema,
    wednesday: normalOpeningHoursEntrySchema,
    thursday: normalOpeningHoursEntrySchema,
    friday: normalOpeningHoursEntrySchema,
    saturday: normalOpeningHoursEntrySchema,
    sunday: normalOpeningHoursEntrySchema,
  })
  .required();

const validationSchemaOpeningHours = yup
  .object({
    openingHoursType: yup
      .mixed<OpeningHoursType>()
      .oneOf([...OPENING_HOURS_TYPES])
      .required(),
    normalOpeningHours: normalOpeningHoursSchema.when('openingHoursType', {
      is: 'normal',
      then: schema =>
        schema.test({
          name: 'overlapping-timespans',
          test: (value, testContext) => {
            const validatedValue = normalOpeningHoursSchema.validateSync(value);
            const { status: hasOverlappingTimespans, overlappingDay } = getOverlappingTimespansInfo(validatedValue);
            if (hasOverlappingTimespans) {
              return testContext.createError({
                path: 'normalOpeningHours',
                message: {
                  id: 'venue-data-collection.venue-information-form.overlapping-opening-times',
                  values: { dayOfWeek: overlappingDay },
                },
              });
            }
            return true;
          },
        }),
    }),
    complicatedOpeningHours: yup
      .array(
        yup
          .object({
            daysOfWeek: yup
              .array(
                yup
                  .mixed<(typeof DAYS_OF_WEEK)[number]>()
                  .oneOf([...DAYS_OF_WEEK])
                  .required(),
              )
              .required(),
            timePairs: yup
              .array(
                yup
                  .object({
                    endMinutes: yup.number().required(),
                    startMinutes: yup.number().required(),
                  })
                  .required(),
              )
              .required(),
            open24Hours: yup.boolean().required(),
          })
          .required(),
      )
      .required(),
    closeDeliveries: yup
      .mixed<CloseDeliveries>()
      .oneOf([...CLOSE_DELIVERIES_OPTIONS])
      .required(),
  })
  .required();

export type ValidDraftVenueOpeningHoursFormValues = yup.InferType<typeof validationSchemaOpeningHours>;

export default validationSchemaOpeningHours;
